import React, { useState } from 'react';
import { useToasts } from 'Context';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import TextArea from 'v3/Components/Form/TextArea';
import { ReviewerLifecycleStatus } from 'v3/Models/Enums';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { buildEndpoint, useDataPoster } from 'Utilities';
import './ReviewerProfile.ReactivateDialog.scss';

type ReviewerProfileReactivateDialogProps = {
  reviewerId: string;
  dialogVisible: boolean;
  toggleDialog: () => void;
  onSuccess: () => void;
};

const ReviewerProfileReactivateDialog: React.FC<ReviewerProfileReactivateDialogProps> = ({
  reviewerId,
  dialogVisible,
  toggleDialog,
  onSuccess,
}) => {
  const toast = useToasts();
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'status'),
    undefined,
    true,
    '3.0'
  );

  const handleClose = () => {
    setNote('');
    setNoteError(undefined);
    toggleDialog();
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    setNoteError(undefined);
    try {
      const resp: any = await postData({
        newStatus: { value: ReviewerLifecycleStatus.Active },
        note,
      });
      if (resp?.status === 200) {
        toast.success('Reviewer has been reactivated');
        onSuccess();
        handleClose();
      } else if (resp?.response?.status === 400) {
        const data = resp.response?.data;
        if (data?.errors) {
          const mappedErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, val]) => {
            const field = key.charAt(0).toLowerCase() + key.slice(1);
            mappedErrors[field] = (val as string[])[0];
          });
          setNoteError(mappedErrors.note);
          toast.error('Please validate the highlighted fields');
        } else {
          toast.error(data?.message ?? 'Failed to reactivate reviewer. Please try again.');
        }
      } else if (resp?.response?.status === 403) {
        toast.error(resp.response?.data?.message ?? 'You do not have permission to perform this action.');
      } else if ((resp?.response?.status ?? 0) >= 500) {
        toast.error('Failed to reactivate reviewer. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      dialogVisible={dialogVisible}
      toggleDialog={handleClose}
      className="reactivate-reviewer-dialog"
      title="Reactivate Reviewer"
      primaryButtonText={isSubmitting ? 'Reactivating...' : 'Reactivate'}
      primaryButtonEnabled={!isSubmitting && note.trim().length > 0}
      OnPrimaryButtonClick={handleConfirm}
      secondaryButtonText="Cancel"
      OnSecondaryButtonClick={handleClose}
    >
      <p className="reactivate-reviewer-dialog__description">
        Are you sure you want to reactivate this reviewer? They will be returned to Active status and will be
        available for case assignment.
      </p>
      <TextArea
        label="Note"
        required
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          setNoteError(undefined);
        }}
        error={noteError}
        rows={5}
      />
    </ConfirmDialog>
  );
};

export default ReviewerProfileReactivateDialog;
